import { UserType } from 'interfaces/user';
import { FaShoppingBag } from 'react-icons/fa';
import { FaBoxesStacked } from 'react-icons/fa6';
import {
  MdExplore,
  MdLibraryBooks,
  MdMenuBook,
  MdOutlineReceiptLong,
  MdPaid,
  MdPieChart,
  MdRequestQuote,
  MdShoppingCart,
  MdWarehouse,
} from 'react-icons/md';
import { getRoutePrefix, INPUTS_ADMIN_PROFILE_ID, VILLA_ADMIN_PROFILE_ID } from 'services/theming';
import { useInputsStateSafe } from 'screens/InputsMarket/providers/InputsStateProvider';
import { useWhiteLabelling } from 'components/WhiteLabellingProvider';
import { paths } from 'config/paths';
import { Permission, Scope, useAccess } from './useAccess';
import { useFeatures } from './useFeatures';
import { useIsFocusHeaderPath } from './useIsFocusHeaderPath';
import useIsViewport from './useIsViewport';
import useMarketAndCatalogRoutes from './useMarketAndCatalogRoutes';
import useRequestQuoteRoute from './useRequestQuoteRoute';
import { useShipmentsOrSalesPath } from './useShipmentsOrSalesPath';
import useUser from './useUser';

const useNavigationItems = () => {
  const { isEnabled } = useFeatures();
  const isConsolidatedInputsEnabled = isEnabled('ConsolidatedInputs');

  const isMobile = useIsViewport();
  const { user } = useUser();
  const { isInputsType, isNileFreshProfile } = useWhiteLabelling();
  const has = useAccess();
  const isFocusHeader = useIsFocusHeaderPath();
  const inputsState = useInputsStateSafe();
  const buyerState = inputsState?.buyerState;
  const requestQuoteRoute = useRequestQuoteRoute(buyerState?.buyerID);
  const prefix = getRoutePrefix();
  const shipmentsOrSalesPath = useShipmentsOrSalesPath();
  const { showMarketRoute, showCatalogRoute } = useMarketAndCatalogRoutes();

  // Early return for logged out state
  if (!user?.userType) {
    return [
      {
        label: 'Market',
        path: `${prefix}?page=${paths.market()}`,
        icon: FaShoppingBag,
      },
      {
        label: 'Orders',
        path: `${prefix}?page=${shipmentsOrSalesPath}`,
        icon: MdOutlineReceiptLong,
      },
      { label: 'Reports', path: `${prefix}?page=${paths.reports()}`, icon: MdPieChart },
      { label: 'News', path: `${prefix}?page=${paths.explore()}`, icon: MdExplore },
    ];
  }

  const isVisible = !isFocusHeader;
  const isSeller = user.userType === UserType.Seller;
  const isAdmin = user.userType === UserType.Admin;
  const isNonSeller = !isSeller;

  const hasCartAccess = has(Permission.Market, Scope.Write);
  const hasMarketAccess = has(Permission.Market, Scope.Read);
  const hasPackhouseAccess = has(Permission.Packhouse, Scope.Read);
  const hasReportsAccess = has(Permission.Reports, Scope.Read);
  const hasSalesAccess = has(Permission.Sales, Scope.Read);
  const hasOperationsAccess = has(Permission.Operations, Scope.Read);
  const hasFinanceAccess = has(Permission.Finance, Scope.Read);
  const hasSellerPaymentsAccess = has(Permission.SellerPayments, Scope.Read);
  const hasSellerLoadoutsAccess = has(Permission.SellerLoadouts, Scope.Read);
  const hasQuotesAllBuyersAccess = has(Permission.QuotesAllBuyers, Scope.Read);

  const hasSelectedInputsBuyer =
    buyerState?.buyerID && ![INPUTS_ADMIN_PROFILE_ID, VILLA_ADMIN_PROFILE_ID].includes(Number(buyerState.buyerID));

  const showCart = isNonSeller && hasCartAccess && !isInputsType && isVisible;
  const showSellerMarket = isSeller && hasMarketAccess && isVisible;
  const showReports = isSeller && hasReportsAccess && isVisible;
  const showPackhouse = isSeller && hasPackhouseAccess && isVisible;
  const showNews = !isInputsType && isVisible && !isAdmin && !isSeller;
  // Quotes outlive the inputs market, so the flag surfaces them to fresh profiles too. A buyer
  // always has their own quotes; an admin without the role only sees quotes raised against their
  // own profile, which is nothing worth a tab.
  const canSeeQuotes = isInputsType || (isConsolidatedInputsEnabled && (!isAdmin || hasQuotesAllBuyersAccess));
  const showQuotes = isNonSeller && hasMarketAccess && canSeeQuotes && isVisible;
  // Raising a quote needs a buyer, which the inputs market is where you pick. Consolidated,
  // that lives on the quotes screen next to its own buyer selector instead of in the nav.
  const showRequestQuote = isNonSeller && hasMarketAccess && isInputsType && isVisible && hasSelectedInputsBuyer;
  const showCreditBalance = isNonSeller && hasMarketAccess && isInputsType && !isMobile;
  const showOrders = !isAdmin && isVisible;
  const showMarket = hasMarketAccess && isVisible;
  const showOperations = isVisible && isAdmin && hasOperationsAccess;
  const showSales = isAdmin && hasSalesAccess && isVisible;
  const showPayments =
    (isAdmin && hasFinanceAccess && isVisible) ||
    (isSeller && isVisible && (hasSellerPaymentsAccess || hasSellerLoadoutsAccess));

  const cartItem = {
    show: showCart,
    label: 'Cart',
    path: paths.cart(),
    icon: MdShoppingCart,
  };

  const navigationConfig = [
    cartItem,
    {
      show: showMarket && (showMarketRoute || showSellerMarket),
      label: 'Market',
      path: showSellerMarket ? paths.sellerMarket() : paths.market(),
      icon: FaShoppingBag,
    },
    {
      show: showMarket && !isSeller && isNileFreshProfile && showCatalogRoute,
      label: 'Catalog',
      path: paths.catalogMarket(),
      icon: MdMenuBook,
    },
    {
      show: showSales,
      label: 'Sales',
      path: paths.sales(),
      icon: MdOutlineReceiptLong,
    },
    {
      show: showOperations,
      label: 'Operations',
      path: paths.operations(),
      icon: MdWarehouse,
    },
    {
      show: showOrders,
      label: 'Orders',
      path: shipmentsOrSalesPath,
      icon: MdOutlineReceiptLong,
    },
    {
      show: showPayments,
      label: 'Payments',
      path: paths.payments(),
      icon: MdPaid,
    },
    {
      show: showPackhouse,
      label: 'Packing',
      path: paths.packhouse(),
      icon: FaBoxesStacked,
    },
    {
      show: showReports,
      label: 'Reports',
      path: paths.reports(),
      icon: MdPieChart,
    },
    {
      show: showNews,
      label: 'News',
      path: paths.explore(),
      icon: MdExplore,
    },
    {
      show: showQuotes,
      label: 'Quotes',
      path: paths.quotes(),
      icon: MdLibraryBooks,
    },
    {
      show: showCreditBalance,
      label: 'Credit Balance',
      path: '',
      icon: MdRequestQuote,
    },
    {
      show: showRequestQuote,
      label: 'Request',
      path: requestQuoteRoute,
      icon: MdRequestQuote,
    },
  ];

  return navigationConfig.filter((item) => item.show).map(({ show, ...item }) => item);
};

export default useNavigationItems;
